import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import * as CUI from '@CUI';
import { LabelWithPopup } from '@common/perach-ui';
import { useActions } from '@common/store-utils/hooks';

import { dictionariesModel, Dictionaries, fixValue } from '@entities/dictionaries';

import { actions, selectors } from '../model';
import { config } from '../config';
import { stepTwoFields } from '../fields';
import { Optional } from '@common/ui';

/**
 * Users bank details for Registration
 * @returns React.FC
 * */
export const BankDetailsForm: React.FC<{
  disabledFields?: Record<string, boolean>;
  shouldHideButtons?: boolean;
}> = ({ disabledFields, shouldHideButtons = false }) => {
  const { t } = useTranslation();
  const {
    updateBankDetails,
    saveBankDetails,
    autoSaveRegistration,
    setIsModalOpen,
  } = useActions(actions);

  const { getDictionaries, getBankBranches } = useActions(
    dictionariesModel.actions
  );

  const bankDetails = selectors.useBankDetails();
  const banksData = dictionariesModel.selectors.useBanks();
  const bankBranchesData = dictionariesModel.selectors.useBankBranches();
  const loaders = dictionariesModel.selectors.useLoaders();

  const selectedBankValue = bankDetails?.bank?.value;
  const branchDicRecord = bankDetails?.branch
    ? { value: bankDetails.branch, text: '' }
    : { value: '', text: '' };

  useEffect(() => {
    getDictionaries();
  }, []);

  useEffect(() => {
    if (selectedBankValue) {
      getBankBranches(selectedBankValue);
    }
  }, [selectedBankValue]);

  const isBranchDisabled =
    !selectedBankValue || !!disabledFields?.[stepTwoFields.branch];

  return (
    <>
      <div className="form-container ph-25">
        <Row className="g-2 gx-5 mt-34">
          <Col className="col-12 col-md-6">
            {banksData.length > 0 && (
              <>
                <LabelWithPopup
                  label={t('BANK')}
                  helpKey={stepTwoFields.bank}
                />
                <CUI.DropDownSelect
                  name={stepTwoFields.bank}
                  isDisabled={!!disabledFields?.[stepTwoFields.bank]}
                  form={config.forms.bankDetails}
                  shouldFilterValues
                  data={banksData}
                  textField="text"
                  searchFields={['value']}
                  placeholder=" "
                  value={fixValue(bankDetails!.bank, banksData)}
                  onChange={(e) => {
                    updateBankDetails({
                      changes: {
                        bank: e.component.value,
                        branch: '',
                      },
                    });
                    const bankValue = e.component.value?.value;
                    if (bankValue) {
                      getBankBranches(bankValue);
                    }
                  }}
                  isRequired
                  requiredMessage={t('FIELD_REQUIRED')}
                />
              </>
            )}
          </Col>
          <Col className="col-12 col-md-6">
            <LabelWithPopup
              label={t('BRANCH')}
              helpKey={stepTwoFields.branch}
            />
            <CUI.DropDownSelect
              _mb40
              name={stepTwoFields.branch}
              isDisabled={isBranchDisabled}
              form={config.forms.bankDetails}
              shouldFilterValues
              data={bankBranchesData}
              textField="text"
              searchFields={['value', 'text']}
              placeholder=" "
              isLoading={loaders?.[Dictionaries.BankBranches] || false}
              value={fixValue(branchDicRecord, bankBranchesData)}
              onChange={(e) =>
                updateBankDetails({
                  changes: { branch: e.component.value?.value || '' },
                })
              }
              isRequired
              requiredMessage={t('FIELD_REQUIRED')}
            />
          </Col>
        </Row>
        <Row className="g-2 gx-5">
          <Col className="col-12 mb-80">
            <LabelWithPopup
              label={t('ACCOUNT_NUMBER')}
              helpKey={stepTwoFields.accountNumber}
            />
            <CUI.Input
              form={config.forms.bankDetails}
              value={bankDetails?.accountNumber}
              name={stepTwoFields.accountNumber}
              isDisabled={!!disabledFields?.[stepTwoFields.accountNumber]}
              onChange={(e) =>
                updateBankDetails({
                  changes: { accountNumber: e.component.value },
                })
              }
              // @ts-ignore
              onBlur={autoSaveRegistration}
              allowedSymbols="numbers"
              isRequired
              maxLength={15}
              requiredMessage={t('FIELD_REQUIRED')}
            />
            <span>
              *באיסוף פרטי בנק אין הבטחה למתן מלגה או חלק ממנה, ופרטי הבנק ישמשו
              רק במידה ותהיה זכאות למלגה. שאר פרטי הבנק ימחקו.
            </span>
          </Col>
        </Row>
        <Optional when={!shouldHideButtons}>
          <Row>
            <Col className="col-12 col-md-9 mb-40">
              <CUI.Button
                _dBlock
                _w100
                _signInButton
                _btnPrimaryPerach
                form={config.forms.bankDetails}
                // @ts-ignore
                onClick={saveBankDetails}
                shouldScrollToInvalidFields
                isDisabled={!!disabledFields?.[stepTwoFields.buttonSubmit]}
                scrollOffset={135}
              >
                {t('NEXT')}
              </CUI.Button>
            </Col>
            <Col className="col-12 col-md-3 mb-40">
              <CUI.Button
                _dBlock
                _w100
                _signInButton
                _btnInfoPerach
                isDisabled={!!disabledFields?.[stepTwoFields.buttonSave]}
                onClick={() => setIsModalOpen(true)}
              >
                {t('SAVE_AND_CLOSE')}
              </CUI.Button>
            </Col>
          </Row>
        </Optional>
      </div>
    </>
  );
};
